import {
  NumberField,
  TextField,
  DatagridConfigurable,
  useCanAccess,
  ReferenceField,
  List,
  AutocompleteArrayInput,
  NumberInput,
  ReferenceArrayInput,
  TextInput,
} from 'react-admin';
import TzDateField from '../../Components/TzDate/TzDateField'; // Assuming you use this for timezone
import { ReactElement } from 'react';
import { ShopScanResource } from '../../Entities/ShopScan';
import { createFilterQuery, DEFAULT_LIST_PAGE_PAGINATION_LIMIT } from '../../utils';
import TzDateTimeInput from '../../Components/TzDate/TzDateTimeInput';
import { useParams } from 'react-router-dom';
import LargePagination from '../../Components/LargePagination';
import {
  getShopScanMappingErrorPath,
  getShopScanMappingErrorRowSx,
  SHOP_SCAN_STATUS_FLAGS,
} from './utils';
import BitmaskTagsField from '../../Components/BitmaskTagsField/BitmaskTagsField';
import BitmaskTagsFilter from '../../Components/BitmaskTagsFilter/BitmaskTagsFilter';
import { Alert } from '@mui/material';

const filters = (canListMerchants: boolean): ReactElement[] =>
  [
    <TextInput key="id" label="ID" source="id" alwaysOn />,
    <BitmaskTagsFilter
      alwaysOn
      key="status"
      source="any.status"
      label="Status"
      flags={SHOP_SCAN_STATUS_FLAGS}
    />,
    canListMerchants && (
      <ReferenceArrayInput
        alwaysOn
        key="merchants"
        label="Merchants"
        source="any.merchant.id"
        reference="merchants"
      >
        <AutocompleteArrayInput
          label="Merchants"
          optionText="attributes.merchant_name"
          filterToQuery={createFilterQuery(['merchant_name'])}
        />
      </ReferenceArrayInput>
    ),
    <NumberInput
      key="total_offers_lt"
      label="Total Offers Less Than"
      source="lessThan.total_offers"
    />,
    <NumberInput
      key="total_offers_gt"
      label="Total Offers Greater Than"
      source="greaterThan.total_offers"
    />,
    <TzDateTimeInput key="start_date_before" label="Starts Before" source="lessThan.start_date" />,
    <TzDateTimeInput key="start_date_after" label="Starts After" source="greaterThan.start_date" />,
    <TzDateTimeInput key="end_date_before" label="Ends Before" source="lessThan.end_date" />,
    <TzDateTimeInput key="end_date_after" label="Ends After" source="greaterThan.end_date" />,
  ].filter((value) => typeof value !== 'boolean');

export default function ScanShow(): ReactElement {
  const { id: scanId } = useParams();

  const merchantAccess = useCanAccess({ action: 'list', resource: 'merchants' });
  const scanAccess = useCanAccess({ action: 'list', resource: 'scans' });
  const canListMerchants = merchantAccess.canAccess ?? false;
  const canListScans = scanAccess.canAccess ?? false;

  const prefetch: string[] = [];
  if (canListMerchants) prefetch.push('merchant');
  if (canListScans) prefetch.push('scan');

  return (
    <List<ShopScanResource>
      title={`Shop Scans for Scan ID: ${scanId}`}
      resource="shop_scans"
      filters={filters(canListMerchants)}
      sort={{ order: 'DESC', field: 'total_offers' }}
      filter={{ 'scan.id': scanId }}
      perPage={DEFAULT_LIST_PAGE_PAGINATION_LIMIT}
      pagination={<LargePagination />}
      queryOptions={{ meta: { prefetch } }}
    >
      <Alert severity="info" sx={{ mb: 1 }}>
        Only rows with mapping errors are clickable.
      </Alert>
      <DatagridConfigurable
        bulkActionButtons={false}
        rowClick={(_id, _basePath, record) =>
          getShopScanMappingErrorPath(record as ShopScanResource)
        }
        rowSx={(record) => getShopScanMappingErrorRowSx(record as ShopScanResource)}
      >
        <BitmaskTagsField
          label="Status"
          source="attributes.status"
          flags={SHOP_SCAN_STATUS_FLAGS}
        />
        <ReferenceField
          source="relationships.merchant.data.id"
          reference="merchants"
          emptyText="Record Missing"
          label="Merchant"
        >
          <TextField source="attributes.merchant_name" />
        </ReferenceField>
        <TzDateField source="attributes.start_date" label="Start Date" showTime />
        <TzDateField source="attributes.end_date" label="End Date" showTime />
        <NumberField source="attributes.total_offers" label="Total Offers" />
        <NumberField source="attributes.new_offers" label="New Offers" />
        <NumberField source="attributes.recovered_offers" label="Recovered Offers" />
        <NumberField source="attributes.removed_offers" label="Removed Offers" />
        <NumberField source="attributes.updated_offers" label="Updated Offers" />
        <NumberField source="attributes.unchanged_offers" label="Unchanged Offers" />
        <NumberField source="attributes.duplicate_offers" label="Duplicate Offers" />
      </DatagridConfigurable>
    </List>
  );
}
